import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {AuthenticationService} from '../../Services/authentication.service';
import {ToolsBoxService} from '../../Services/tools.service';
import {UserService} from '../../Services/user.service';
import { NotificationComponent } from '../../Notification/notification.component';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface LoginForm {
  userName: FormControl<string>;
  userPassword: FormControl<string>;
  userConfirmPassword: FormControl<string>;
}

@Component({
    selector: 'login',
    templateUrl: 'login.component.html',
    styleUrls: ['login.component.css'],
    imports: [NotificationComponent, ReactiveFormsModule, NgClass],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent implements OnInit {

  private readonly toolsBoxService = inject(ToolsBoxService);
  private readonly authService     = inject(AuthenticationService);
  private readonly userService     = inject(UserService);
  private readonly router          = inject(Router);
  private readonly destroyRef      = inject(DestroyRef);
  private readonly cdr             = inject(ChangeDetectorRef);

  // Signal from ToolsBoxService — available immediately because inject() runs before field init
  readonly backEndVersion = this.toolsBoxService.backEndVersion;

  userForm: FormGroup<LoginForm>;
  version = '';
  errorCode = 0;
  errorMessage: string;
  firstLogin = false;
  nbErrors = 0;

  constructor() {
    this.version  = this.toolsBoxService.getVersion();

    this.userForm = new FormGroup<LoginForm>({
      userName:           new FormControl(this.toolsBoxService.getStoredUserLogin() ?? '', { nonNullable: true, validators: [Validators.required] }),
      userPassword:       new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      userConfirmPassword:new FormControl('', { nonNullable: true, validators: [Validators.required] })
    });

    this.userForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.userForm.errors) {
        this.nbErrors = Object.keys(this.userForm.errors).length;
      }
    });
  }

  get userPassword(): string       { return this.userForm.controls.userPassword.value; }
  get userConfirmPassword(): string { return this.userForm.controls.userConfirmPassword.value; }

  ngOnInit() {
    if (this.userForm.controls.userName.value) {
      this.errorCode = 401;
      this.errorMessage = 'Your authenticate is not valid anymore';
      this.cdr.markForCheck();
    }
  }

  onClose() {
    this.errorCode = 0;
  }

  initChangePwdRequest(): void {
    this.firstLogin = true;
    this.userForm.controls.userPassword.setValue('');
  }

  changePassword(): void {
    const pwd = this.userForm.controls.userPassword.value;
    const confirmPwd = this.userForm.controls.userConfirmPassword.value;
    if (pwd === '' || pwd !== confirmPwd) { return; }

    this.toolsBoxService.tellActionIsInProgress(true);
    this.userService.updateUserPwd(this.userForm.controls.userName.value.trim(), pwd)
      .subscribe({
        next: () => {
          this.toolsBoxService.tellActionIsInProgress(false);
          this.router.navigate(['Dashboard']);
          this.firstLogin = false;
          this.toolsBoxService.isLoggedIn.set(true);
          this.errorCode = 0;
          sessionStorage.setItem('user_login', this.userForm.controls.userName.value.trim());
        },
        error: (err) => {
          this.toolsBoxService.tellActionIsInProgress(false);
          this.firstLogin = false;
          this.errorCode = 500;
          this.errorMessage = err.status ? err.error : err.message;
          this.cdr.markForCheck();
        }
      });
  }

  authentication(salt: string): void {
    this.toolsBoxService.tellActionIsInProgress(true);
    this.authService.sendCredential(
        this.userForm.controls.userName.value.trim(),
        this.userForm.controls.userPassword.value,
        salt
      ).subscribe(
        (data) => {
          this.toolsBoxService.tellActionIsInProgress(false);
          if (data === 0) {
            this.router.navigate(['Dashboard']);
            this.errorCode = 0;
          } else {
            this.initChangePwdRequest();
          }
        },
        (err) => {
          this.toolsBoxService.tellActionIsInProgress(false);
          this.errorCode = 500;
          this.errorMessage = err.status ? err.error : err.message;
          this.cdr.markForCheck();
        });
  }

  getSalt(): void {
    this.toolsBoxService.tellActionIsInProgress(true);
    this.authService.getSalt(this.userForm.controls.userName.value.trim())
      .subscribe(
        (data) => { this.authentication(data); },
        ()  => { this.authentication(''); });
  }
}
